import CookieUtil from "../storage/cookieUtil";
import { COUNTRIES } from "../../constants/common/common";
import { LAST_VISITED_INTERNATIONAL_CITY, SELECTED_INTERNATIONAL_CITY } from "../../constants/storage";
import { filterAvailableCity } from "../plp/common";

/**
 * Formats a Date as DD-MM-YYYY for the serviceability API selectedDate field.
 *
 * @param {Date} [date]
 * @returns {string}
 */
const formatServiceabilitySelectedDate = (date = new Date()) => {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

/**
 * Builds checkServiceAvailabilityForPincode request payload from cart order list.
 * Always includes every product id so one pinCode request clubs the full cart.
 * Missing/_NA_ delivery dates fall back to today (API requires selectedDate).
 *
 * @param {Array} orderList
 * @param {string} pinCode
 * @returns {{ productIds: Array<{ pid: string, selectedDate: string }>, pinCode: string }}
 */
export function buildServiceabilityPayload(orderList = [], pinCode = "") {
  const fallbackSelectedDate = formatServiceabilitySelectedDate();
  const productIds = orderList
    .map((orderItem) => {
      const giftItem = orderItem?.giftItem || orderItem;
      const pid = giftItem?.mainproduct?.productId;
      const selectedDate = giftItem?.shippingDetails?.deliveryDate?.fullDeliveryDate;

      if (!pid) {
        return null;
      }

      return {
        pid,
        countryGeoId: giftItem?.countryGeoId || "IND",
        selectedDate: !selectedDate || selectedDate === "_NA_" ? fallbackSelectedDate : selectedDate,
      };
    })
    .filter(Boolean);

  return { productIds, pinCode };
}

/**
 * Checks whether a checkout gift item belongs to domestic India catalog.
 *
 * @param {Object} giftItem
 * @returns {boolean}
 */
export function isDomesticGiftItem(giftItem = {}) {
  return giftItem?.countryGeoId === "IND" || giftItem?.productCatalogId === COUNTRIES.INDIA;
}

/**
 * Returns whether a checkout gift item has a saved delivery address selected.
 *
 * @param {Object} giftItem
 * @returns {boolean}
 */
export const hasCheckoutGiftItemSavedAddress = (giftItem = {}) => {
  const addressDetails = giftItem?.selectedAddressDetails || {};
  const contactMechId = addressDetails?.selectedContactMechId;

  return (
    Boolean(addressDetails?.postalDtlsOfContactMechIdToName) &&
    Boolean(contactMechId) &&
    contactMechId !== "newaddress" &&
    contactMechId !== "editaddress"
  );
};

/**
 * When the first cart item is pincode-only, returns its pincode so all domestic items
 * share one serviceability pin after setCartItemValues sync (stale address pins are ignored).
 *
 * @param {Array} orderList
 * @returns {string}
 */
export function resolveUnifiedDomesticPincodeFromFirstItem(orderList = []) {
  if (orderList.length <= 1) {
    return "";
  }

  const firstGiftItem = orderList[0]?.giftItem;

  if (!firstGiftItem || !isDomesticGiftItem(firstGiftItem) || hasCheckoutGiftItemSavedAddress(firstGiftItem)) {
    return "";
  }

  return firstGiftItem?.mainproduct?.pincode || "";
}

/**
 * Resolves pincode/cityId used for checkout serviceability APIs.
 * Prefer the selected delivery-address pin over per-product location-lock pin so a
 * multi-item cart with one shared address hits products/serviceable once (all pids).
 * When `unifiedPincode` is set (first item pincode-only multi-item cart), domestic items
 * use that pin instead of stale address postal codes left after pincode sync.
 *
 * @param {Object} giftItem
 * @param {string} [catalogId]
 * @param {string} [unifiedPincode]
 * @returns {string}
 */
export function resolveCheckoutServiceabilityPinCode(giftItem = {}, catalogId = "", unifiedPincode = "") {
  if (unifiedPincode && isDomesticGiftItem(giftItem)) {
    return unifiedPincode;
  }

  const resolvedCatalogId = catalogId || giftItem?.productCatalogId || "";

  const fromAddress =
    giftItem?.selectedAddressDetails?.postalDtlsOfContactMechIdPostalCode ||
    giftItem?.selectedAddressDetails?.postalCode ||
    "";

  if (fromAddress) {
    return fromAddress;
  }

  const fromProduct = giftItem?.mainproduct?.pincode || "";

  if (fromProduct) {
    return fromProduct;
  }

  if (isDomesticGiftItem(giftItem)) {
    return "";
  }

  if (typeof window === "undefined") {
    return "";
  }

  const cityIdFromCookie = filterAvailableCity(CookieUtil.getCookie(SELECTED_INTERNATIONAL_CITY), resolvedCatalogId);

  if (cityIdFromCookie) {
    return cityIdFromCookie;
  }

  return CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_CITY) || "";
}

/**
 * Resolves serviceability pin for a gift item using multi-item cart context when provided.
 *
 * @param {Object} giftItem
 * @param {string} [catalogId]
 * @param {Array} [orderList]
 * @returns {string}
 */
export function resolveServiceabilityPinCodeForGiftItem(giftItem = {}, catalogId = "", orderList = []) {
  const unifiedPincode = resolveUnifiedDomesticPincodeFromFirstItem(orderList);
  return resolveCheckoutServiceabilityPinCode(giftItem, catalogId, unifiedPincode);
}

/**
 * Groups checkout order list entries by resolved serviceability pincode/cityId.
 * When multiple items share the same saved-address pin, they stay in one group so
 * one serviceable request carries every pid.
 *
 * @param {Array} orderList
 * @returns {Map<string, Array>}
 */
export function groupOrderListByServiceabilityPinCode(orderList = []) {
  const pinCodeGroups = new Map();
  const unifiedPincode = resolveUnifiedDomesticPincodeFromFirstItem(orderList);

  orderList.forEach((order) => {
    const giftItem = order?.giftItem;
    const pinCode = resolveCheckoutServiceabilityPinCode(giftItem, giftItem?.productCatalogId, unifiedPincode);
    if (!pinCode) {
      return;
    }

    if (!pinCodeGroups.has(pinCode)) {
      pinCodeGroups.set(pinCode, []);
    }

    pinCodeGroups.get(pinCode).push(order);
  });

  return pinCodeGroups;
}

/**
 * Normalizes checkout date strings for serviceability comparison.
 *
 * @param {string} date
 * @returns {string}
 */
const normalizeServiceabilityDate = (date = "") => date.replace(/\//g, "-").trim();

/**
 * Compares serviceability selected and earliest available dates.
 *
 * @param {string} dateA
 * @param {string} dateB
 * @returns {boolean}
 */
export const areServiceabilityDatesEqual = (dateA = "", dateB = "") => {
  if (!dateA || !dateB) {
    return false;
  }

  return normalizeServiceabilityDate(dateA) === normalizeServiceabilityDate(dateB);
};

/**
 * Resolves serviceability API item for a checkout gift item.
 *
 * @param {Object} giftItem
 * @param {Record<string, Array>} serviceItemsByPinCode
 * @returns {Object|null}
 */
export function getServiceabilityItemForGiftItem(giftItem = {}, serviceItemsByPinCode = {}, orderList = []) {
  const pinCode = resolveServiceabilityPinCodeForGiftItem(giftItem, giftItem?.productCatalogId, orderList);

  if (!pinCode) {
    return null;
  }

  const serviceItems = serviceItemsByPinCode[pinCode] || [];
  const pid = giftItem?.mainproduct?.productId;

  return serviceItems.find((item) => item.pid === pid) || null;
}

/**
 * Returns whether serviceability status has been received for a gift item.
 * Items without a serviceability pincode are treated as resolved immediately.
 *
 * @param {Object} giftItem
 * @param {Object} checkoutLandingServiceability
 * @returns {boolean}
 */
export function hasServiceabilityStatusForGiftItem(giftItem = {}, checkoutLandingServiceability = {}, orderList = []) {
  const pinCode = resolveServiceabilityPinCodeForGiftItem(giftItem, giftItem?.productCatalogId, orderList);

  if (!pinCode) {
    return true;
  }

  const serviceItemsByPinCode = checkoutLandingServiceability?.serviceItemsByPinCode || {};

  return Object.prototype.hasOwnProperty.call(serviceItemsByPinCode, pinCode);
}

/**
 * Returns whether cart expiry should trigger for a gift item.
 * Cart expiry applies only after serviceability resolves and earliestAvailableDate equals selectedDate.
 *
 * @param {Object} giftItem
 * @param {Object} checkoutLandingServiceability
 * @returns {boolean}
 */
export function isCartExpiryEligibleForGiftItem(giftItem = {}, checkoutLandingServiceability = {}, orderList = []) {
  if (!hasServiceabilityStatusForGiftItem(giftItem, checkoutLandingServiceability, orderList)) {
    return false;
  }

  const serviceItemsByPinCode = checkoutLandingServiceability?.serviceItemsByPinCode || {};
  const serviceItem = getServiceabilityItemForGiftItem(giftItem, serviceItemsByPinCode, orderList);

  if (!serviceItem?.earliestAvailableDate) {
    return false;
  }

  const selectedDate = giftItem?.shippingDetails?.deliveryDate?.fullDeliveryDate || serviceItem?.selectedDate;

  if (!selectedDate || selectedDate === "_NA_") {
    return false;
  }

  return areServiceabilityDatesEqual(selectedDate, serviceItem.earliestAvailableDate);
}

/**
 * Checks if a serviceability item is fully unavailable (no delivery date alternative).
 *
 * @param {Object} item
 * @returns {boolean}
 */
export function isServiceabilityItemFullyUnavailable(item = {}) {
  return !item.isDeliverable && !item.earliestAvailableDate;
}

/**
 * Builds a map of cart item indexes that are fully unavailable from serviceability response.
 *
 * @param {Array} serviceItems
 * @param {Array} orderList
 * @returns {Record<number, boolean>}
 */
export function buildOutOfStockByCartItemIndex(serviceItems = [], orderList = []) {
  const outOfStockMap = {};
  const serviceItemByPid = new Map(serviceItems.map((item) => [item.pid, item]));

  orderList.forEach((order) => {
    const pid = order?.giftItem?.mainproduct?.productId;
    const cartItemIndex = order?.giftItem?.cartItemIndex;
    const serviceItem = serviceItemByPid.get(pid);

    if (
      cartItemIndex !== undefined &&
      cartItemIndex !== null &&
      serviceItem &&
      isServiceabilityItemFullyUnavailable(serviceItem)
    ) {
      outOfStockMap[cartItemIndex] = true;
    }
  });

  return outOfStockMap;
}

/**
 * Returns true when every cart item is marked out of stock.
 *
 * @param {Record<number, boolean>} outOfStockByCartItemIndex
 * @param {Array} orderList
 * @returns {boolean}
 */
export function isCartFullyOutOfStock(outOfStockByCartItemIndex = {}, orderList = []) {
  if (!orderList.length) {
    return false;
  }

  return orderList.every((order) => Boolean(outOfStockByCartItemIndex[order?.giftItem?.cartItemIndex]));
}

/**
 * Returns true when some cart items are out of stock but not all.
 *
 * @param {Record<number, boolean>} outOfStockByCartItemIndex
 * @param {Array} orderList
 * @returns {boolean}
 */
export function isCartPartiallyOutOfStock(outOfStockByCartItemIndex = {}, orderList = []) {
  if (!orderList.length) {
    return false;
  }

  const outOfStockCount = orderList.filter((order) =>
    Boolean(outOfStockByCartItemIndex[order?.giftItem?.cartItemIndex]),
  ).length;

  return outOfStockCount > 0 && outOfStockCount < orderList.length;
}

/**
 * Returns checkout order list entries that are serviceable (in stock).
 *
 * @param {Array} orderList
 * @param {Record<number, boolean>} outOfStockByCartItemIndex
 * @returns {Array}
 */
export function getServiceableCheckoutOrderList(orderList = [], outOfStockByCartItemIndex = {}) {
  return orderList.filter((order) => !outOfStockByCartItemIndex[order?.giftItem?.cartItemIndex]);
}

/**
 * Evaluates serviceability API response for saved-address selection UI.
 *
 * @param {Array<{ pid: string, isDeliverable: boolean, earliestAvailableDate: string|null }>} items
 * @returns {"error"|"warning"|"success"}
 */
export function evaluateServiceabilityResponse(items = []) {
  if (!items.length) {
    return "success";
  }

  /**
   * Checks if the item is fully unavailable.
   * @param {Object} item
   * @returns {boolean}
   */
  const isFullyUnavailable = (item) => isServiceabilityItemFullyUnavailable(item);

  /**
   * Checks if the item is fully available.
   * @param {Object} item
   * @returns {boolean}
   */
  const isFullyAvailable = (item) => item.isDeliverable && Boolean(item.earliestAvailableDate);

  /**
   * Checks if the item has a schedule warning.
   * @param {Object} item
   * @returns {boolean}
   */
  const hasScheduleWarning = (item) =>
    (!item.isDeliverable && Boolean(item.earliestAvailableDate)) || (!item.isDeliverable && item.isOutOfStock);

  if (items.every(isFullyUnavailable)) {
    return "error";
  }

  if (items.every(isFullyAvailable)) {
    return "success";
  }

  if (items.some(hasScheduleWarning)) {
    return "warning";
  }

  return "error";
}

/**
 * Resolves the pincode/cityId value used for serviceability checks.
 *
 * @param {Object} addressObj
 * @param {string} countryId
 * @returns {string}
 */
export function getServiceabilityPincodeForCountry(addressObj = {}) {
  return addressObj?.postalCode || "";
}

/**
 * Returns form error field keys for serviceability location errors.
 *
 * @param {string} countryId
 * @returns {{ errorKey: string, errorMsgKey: string }}
 */
export function getServiceabilityFieldErrorKeys(countryId = "IND") {
  if (countryId === "IND") {
    return { errorKey: "postalCodeError", errorMsgKey: "postalCodeErrorMsg" };
  }

  return { errorKey: "cityError", errorMsgKey: "cityErrorMsg" };
}

/**
 * Applies serviceability location error to address form state.
 *
 * @param {Object} addressObj
 * @param {string} countryId
 * @param {string} errorMessage
 * @returns {Object}
 */
export function withServiceabilityLocationError(addressObj = {}, countryId = "IND", errorMessage = "") {
  const { errorKey, errorMsgKey } = getServiceabilityFieldErrorKeys(countryId);

  return {
    ...addressObj,
    [errorKey]: true,
    [errorMsgKey]: errorMessage,
  };
}

/**
 * Clears serviceability location error from address form state.
 *
 * @param {Object} addressObj
 * @param {string} countryId
 * @returns {Object}
 */
export function withoutServiceabilityLocationError(addressObj = {}, countryId = "IND") {
  const { errorKey, errorMsgKey } = getServiceabilityFieldErrorKeys(countryId);

  return {
    ...addressObj,
    [errorKey]: false,
    [errorMsgKey]: "",
  };
}
